/**
 * Angular imports.
 */
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

/**
 * Constant imports.
 */
import { CREATION_TEXT_TYPE } from '../../../constants/app.constants';

/**
 * Component used to add the live text in the fields.
 */
@Component({
  selector: 'app-creation-texting',
  templateUrl: './creation-texting.component.html',
  styleUrls: ['./creation-texting.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreationTextingComponent implements OnInit, AfterViewInit {

  /**
   * Element reference for the typing input.
   */
  @ViewChild('typingInput', { static: false }) typingInput: ElementRef;

  /**
   * Pre text.
   * Type of forms available.
   * Max length validation.
   * Typing label for the form.
   * Typing placeholder for the form.
   * Submit button type text for the form.
   * Delete popup heading.
   * Delete popup paragraph.
   * Delete popup cancel button text.
   * Delete popup Accept button text.
   */
  @Input() readonly preText: string;
  @Input() formType = 0;
  @Input() maxLength = 150;
  @Input() typingLabel: string;
  @Input() typingPlaceholder: string;
  @Input() buttonText: string;
  @Input() deletePopupHeading: string;
  @Input() deletePopupParagraph: string;
  @Input() deletePopupCancelText: string;
  @Input() deletePopupDeleteText: string;

  /**
   * Emit the cancel event outside.
   * Emit the delete existing event outside.
   * Emit the live typing event outside.
   */
  @Output() onCancelTyping: EventEmitter<void> = new EventEmitter();
  @Output() onDeleteTyping: EventEmitter<void> = new EventEmitter();
  @Output() onTypingComplete: EventEmitter<string> = new EventEmitter();
  @Output() emitTextOnFocus: EventEmitter<void> = new EventEmitter();

  /**
   * FormGroup for the typing.
   * Typing delete modal for m_web.
   * Creation text type constant.
   */
  public typingForm: FormGroup;
  public isTypingDeleteModal: boolean;
  public creationTextType = CREATION_TEXT_TYPE;

  /**
   * Form submitted flag.
   */
  private submitted = false;

  /**
   * Necessary instances.
   */
  constructor(private formBuilder: FormBuilder) { }

  /**
   * Initialize the reactive form for typing.
   */
  ngOnInit(): void {
    this.typingForm = this.formBuilder.group({
      typing: [this.preText, [Validators.required, Validators.maxLength(this.maxLength)]],
    });
  }

  /**
   * Add auto focus in the text area.
   */
  ngAfterViewInit() {
    this.typingInput.nativeElement.focus();
  }

  /**
   * Cancel the existing typing.
   */
  cancelTyping(): void {
    this.onCancelTyping.emit();
  }

  /**
   * Delete the existing typing.
   */
  deleteTyping(): void {
    this.resetTyping();
    this.onDeleteTyping.emit();
  }

  /**
   * On form submit.
   */
  onSubmitTyping(): void {
    this.submitted = true;
    if (this.typingForm.invalid) {
      return;
    }

    this.typingComplete(this.typingForm.value.typing);
  }

  /**
   * Delete the existing words.
   */
  emitDeleteEvent(): void {
    this.isTypingDeleteModal = false;
    this.deleteTyping();
  }

  /**
   * Reset the form.
   */
  resetTyping(): void {
    this.typingForm.reset({ typing: '' });
  }

  /**
   * Emit when typing is done.
   */
  typingComplete(value: string): void {
    this.onTypingComplete.emit(value.trim());
  }

  /**
   * Emit every keystroke.
   */
  onTypingChange(event: string): void {
    this.onTypingComplete.emit(event);
  }

  /**
   * Emit when in focus.
   */
  onFocus(): void {
    this.emitTextOnFocus.emit();
  }
}
